/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

const renderChildren = (children) => {
  if (!children) {
    return <></>;
  }

  const elements = Array.isArray(children) ? children : [children];
  return <>{elements.map((child) => child)}</>;
};

export const PodTapper = ({
  children, fitWidth, zIndex, pointerNone, aPointer, buttonPointer, spanPointer
}) => {
  const PodTapperClassnames = classNames({
    'sui-w-fit': fitWidth,
    'sui-z-20': zIndex,
    'sui-pointer-events-none': pointerNone,
    '[&_a]:sui-pointer-events-auto': aPointer,
    '[&_button]:sui-pointer-events-auto': buttonPointer,
    '[&_span]:sui-pointer-events-auto': spanPointer
  });

  return (
    <div
      className={PodTapperClassnames}
    >
      {renderChildren(children)}
    </div>
  );
};

/**
 * ?This Layout Piece seems to be too Generic? A flex container for subcomponents
 * @param children
 * @param columnAlign
 * @param noPadding - stops default padding style.
 * @param relative
 * @param margin
 * @param alignTop
 * @param alignEnd
 * @param gap - Spacing between product labels. As default, the minimum spacing must be of $spacing-2 = sui-gap-2.
 * @param fullHeight
 * @param display
 * @param grid
 * @param gridColumns
 * @param order
 * @param flex
 * @param marginTop
 * @param zIndex
 * @param fitWidth
 * @param aPointer
 * @param pointerNone
 * @param buttonPointer
 * @param padding
 * @param layout - layout object that contains display, grid, order, flex and justifyContent
 * @param spacing - spacing object that contains gap, padding and margin
 * @param positioning - positioning object that contains position and zIndex
 * @param sizing - sizing object that contains width and height
 * @param interaction - interaction object that contains pointerEvents
 * @param className - additional class names to be added to the component and override the default and custom styles
 * @returns {Element}
 * @constructor
 */
export const PodSection = ({
  children,
  columnAlign,
  noPadding,
  relative,
  margin,
  alignTop,
  alignEnd,
  gap,
  fullHeight,
  display,
  grid,
  gridColumns,
  order,
  flex,
  marginTop,
  zIndex,
  fitWidth,
  aPointer,
  pointerNone,
  buttonPointer,
  padding,
  layout = {},
  spacing = {},
  positioning = {},
  sizing = {},
  interaction = {},
  className,
}) => {

  const hasDisplayGrid = typeof display === 'string' && display.includes('sui-grid');

  const displayLookup = {
    'sui-flex': 'sui-flex',
    'sui-grid': 'sui-grid',
    'sui-block': 'sui-block',
  };

  const justifyContentLookup = {
    'sui-justify-start': 'sui-justify-start',
    'sui-justify-center': 'sui-justify-center',
    'sui-justify-end': 'sui-justify-end',
  };

  const displayStyle = displayLookup[layout?.display];
  const { grid: gridStyle, order: orderStyle, flex: flexStyle } = layout;
  const justifyContentStyle = justifyContentLookup[layout?.justifyContent];

  const layoutClasses = classNames(
    displayStyle,
    gridStyle,
    orderStyle,
    flexStyle,
    justifyContentStyle);

  const {
    gap: gapSpacing,
    padding: paddingSpacing,
    margin: marginSpacing
  } = spacing;
  const spacingClasses = classNames(
    gapSpacing,
    paddingSpacing,
    marginSpacing
  );

  const positioningClasses = classNames({
    'sui-relative': positioning?.position === 'sui-relative',
    'sui-z-20': positioning?.zIndex === 'sui-z-20',
  });

  const sizingClasses = classNames({
    'sui-w-full': sizing?.width === 'sui-w-full',
    'sui-w-fit': sizing?.width === 'sui-w-fit',
    'sui-h-full': sizing?.height === 'sui-h-full',
  });

  const { pointerEvents } = interaction;
  const interactionClasses = classNames(pointerEvents);

  // TODO: We will keep the props as legacy for now, but we will remove them in the near future.
  // so that we only allow the layout, spacing, positioning, sizing, interaction and className props.
  const podSectionPropsClasses = classNames(
    display, gridColumns, gap, grid, order, flex, padding, marginTop, {
      'sui-gap-4': !gap && !gapSpacing,
      'sui-p-2': !noPadding && !padding && !paddingSpacing,
      'sui-flex': !display && !displayStyle,
      'sui-grid-cols-1': !gridColumns && hasDisplayGrid,
      'sui-flex-col': columnAlign,
      'sui-relative': relative,
      'sui-m-2': margin,
      'sui-justify-start': alignTop,
      'sui-justify-end': alignEnd,
      'sui-z-20': zIndex,
      'sui-w-fit': fitWidth,
      '[&_a]:sui-pointer-events-auto': aPointer,
      'sui-pointer-events-none': pointerNone,
      '[&_button]:sui-pointer-events-auto': buttonPointer,
      'sui-h-full': fullHeight,
    }
  );

  const podSectionClasses = classNames(
    podSectionPropsClasses,
    layoutClasses,
    spacingClasses,
    positioningClasses,
    sizingClasses,
    interactionClasses
  );

  return (
    <div className={className || podSectionClasses} data-testid="pod-section">
      {renderChildren(children)}
    </div>
  );
};

PodSection.propTypes = {
  columnAlign: PropTypes.bool,
  noPadding: PropTypes.bool,
  padding: PropTypes.string,
  relative: PropTypes.bool,
  margin: PropTypes.bool,
  alignTop: PropTypes.bool,
  alignEnd: PropTypes.bool,
  gridColumns: PropTypes.string,
  /* stencil display class, i.e. sui-block, sui-grid; default: sui-flex */
  display: PropTypes.string,
  grid: PropTypes.string,
  gap: PropTypes.string,
  order: PropTypes.string,
  flex: PropTypes.string,
  marginTop: PropTypes.string,
  zIndex: PropTypes.bool,
  fitWidth: PropTypes.bool,
  aPointer: PropTypes.bool,
  pointerNone: PropTypes.bool,
  buttonPointer: PropTypes.bool,
  /**
   * additional class names to be added to the component and override the default and custom styles
   * @param {string} className - CSS class names to apply to the PodSection.
   */
  className: PropTypes.string,
  /**
   * layout object that contains display, grid, order, flex and justifyContent
   * @param {object} layout - Object to configure the layout of the PodSection.
   */
  layout: PropTypes.shape({
    display: PropTypes.oneOf(['sui-flex', 'sui-grid', 'sui-block']),
    grid: PropTypes.string,
    order: PropTypes.string,
    flex: PropTypes.string,
    justifyContent: PropTypes.oneOf(['sui-justify-start', 'sui-justify-center', 'sui-justify-end'])
  }),
  /**
   * spacing object that contains gap, padding and margin
   * @param {object} spacing - Object to configure the spacing of the PodSection.
   */
  spacing: PropTypes.shape({
    gap: PropTypes.string,
    padding: PropTypes.string,
    margin: PropTypes.string,
  }),
  /**
   * positioning object that contains position and zIndex
   * @param {object} positioning - Object to configure the positioning of the PodSection.
   */
  positioning: PropTypes.shape({
    position: PropTypes.oneOf(['sui-relative']),
    zIndex: PropTypes.oneOf(['sui-z-20'])
  }),
  /**
   * sizing object that contains width and height
   * @param {object} sizing - Object to configure the sizing of the PodSection.
   */
  sizing: PropTypes.shape({
    width: PropTypes.oneOf(['sui-w-full', 'sui-w-fit']),
    height: PropTypes.oneOf(['sui-h-full', 'sui-h-fit'])
  }),
  /**
   * interaction object that contains pointerEvents
   * @param {object} interaction - Object to configure the interaction of the PodSection.
   */
  interaction: PropTypes.shape({
    pointerEvents: PropTypes.string,
  }),
};
PodSection.defaultProps = {
  columnAlign: false,
  noPadding: false,
  padding: false,
  relative: false,
  margin: false,
  alignTop: false,
  alignEnd: false,
  gridColumns: null,
  display: null,
  grid: null,
  gap: null,
  order: null,
  flex: null,
  marginTop: null,
  zIndex: false,
  fitWidth: false,
  aPointer: false,
  pointerNone: false,
  buttonPointer: false,
  className: null,
  layout: {
    display: null,
    grid: null,
    order: null,
    flex: null,
    justifyContent: null
  },
  spacing: {
    gap: null,
    padding: null,
    margin: null
  },
  positioning: {
    position: null,
    zIndex: null
  },
  sizing: {
    width: null,
    height: null
  },
  interaction: {
    pointerEvents: null
  }
};

export const PodHeader = ({ children, noPadding, minHeight, justifyBetween }) => {
  const podHeaderClassnames = classNames(
    'sui-flex', {
      'sui-px-2 sui-pb-2': !noPadding,
      'sui-justify-between': justifyBetween
    }
  );

  return (
    <div
      className={podHeaderClassnames}
      style={{ minHeight }}
      data-testid="pod-header"
    >
      {renderChildren(children)}
    </div>
  );
};

export const PodColumn = ({ children, minWidth, noPadding }) => {
  const podColumnClasses = classNames(
    'sui-flex sui-flex-col sui-relative', {
      'sui-p-1': !noPadding
    }
  );

  return (
    <div
      className={podColumnClasses}
      style={{ minWidth }}
      data-testid="pod-column"
    >
      {renderChildren(children)}
    </div>
  );
};

export const PodRow = ({ children, noPadding, rowReverse }) => {
  const podRowClasses = classNames(
    'sui-flex sui-w-full sui-items-center',
    rowReverse ? 'sui-flex-row-reverse' : 'sui-flex-row', {
      'sui-py-2': !noPadding
    }
  );

  return (
    <div
      className={podRowClasses}
      data-testid="pod-row"
    >
      {renderChildren(children)}
    </div>
  );
};

/**
 * Dynamic Pod wrapper that displays child elements in a Row on small screens,
 * and automatically shifts to display children in a Column on med. to lg. screens.
 * @param children - child elements to be displayed in row or column
 * @param minWidth - set minWidth style on top level wrapper element
 * @param gap - set gap style between elements. A default, the minimum spacing must be of 16px = sui-gap-4.
 * @param noPadding - stops default padding style.
 * @param grid - set grid style on top level wrapper element
 * @returns {Element}
 */
export const PodRowToColumn = ({
  children, minWidth, gap, noPadding, grid
}) => {
  const podRowClasses = classNames(
    'sui-grid',
    grid || 'sui-grid-cols-2 md:sui-grid-cols-1',
    gap || 'sui-gap-4', {
      'sui-p-2': !noPadding,
    }
  );

  return (
    <div
      className={podRowClasses}
      style={{ minWidth }}
      data-testid="pod-column"
    >
      {renderChildren(children)}
    </div>
  );
};

export const PodFooter = ({ children, noPadding, margin }) => {
  const podFooterClasses = classNames(
    margin || 'sui-mt-auto', {
      'sui-px-2': !noPadding,
    });

  return (
    <div
      className={podFooterClasses}
      data-testid="pod-footer"
    >
      {renderChildren(children)}
    </div>
  );
};

// Note: position prop default is 'top-left'; but position can also be 'top-right'.
export const PodSticker = ({ children, position = 'top-left' }) => {
  const podStickerClasses = classNames(
    'sui-absolute sui-flex sui-z-10',
    position === 'top-left' ? 'sui-left-0' : 'sui-right-0'
  );

  return (
    <div
      className={podStickerClasses}
      data-testid="pod-sticker"
    >
      {renderChildren(children)}
    </div>
  );
};

// TODO: reduce complexity of the prop values, while still controlling the spacing classes allowed to be used
// NOTE: if we allow any spacing values to be applied, do we need a PodSpacer class at all or do we just allow
// the users to make their own spacer div elements?
/**
 *
 * @param children
 * @param padding - sets same padding for all screen-sizes/breakpoints. see prop comment for more.
 * @param smlScreenPadding - sets padding for $screens-sm sizes only; see "breakpoints" in stencil design system.
 * @param minHeight - sets a static inline style of minHeight on the top level `PodSpacer` element
 * @param disable - disables the element, returns `null` instead.
 * @param fullwidth
 * @param display
 * @param margin
 * @returns {React.JSX.Element|null}
 */
export const PodSpacer = ({
  children, padding, smlScreenPadding, minHeight, disable, fullwidth, display, margin
}) => {
  if (disable) return null;

  const style = minHeight ? { minHeight } : {};

  const podSpacerClasses = classNames(
    margin, display, {
      'sui-p-1': padding.includes('ALL'),
      'sui-pl-1': padding.includes('LEFT'),
      'sui-pr-1': padding.includes('RIGHT'),
      'sui-pt-1': padding.includes('TOP'),
      'sui-pb-1': padding.includes('BOTTOM'),

      'sui-p-2': padding.includes('ALL2'),
      'sui-pl-2': padding.includes('L2'),
      'sui-pr-2': padding.includes('R2'),
      'sui-pt-2': padding.includes('T2'),
      'sui-pb-2': padding.includes('B2'),

      'sui-p-4': padding.includes('ALL4'),
      'sui-pl-4': padding.includes('L4'),
      'sui-pr-4': padding.includes('R4'),
      'sui-pt-4': padding.includes('T4'),
      'sui-pb-4': padding.includes('B4'),

      'sm:sui-p-1': smlScreenPadding.includes('ALL'),
      'sm:sui-pl-1': smlScreenPadding.includes('LEFT'),
      'sm:sui-pr-1': smlScreenPadding.includes('RIGHT'),
      'sm:sui-pt-1': smlScreenPadding.includes('TOP'),
      'sm:sui-pb-1': smlScreenPadding.includes('BOTTOM'),

      'sm:sui-p-2': smlScreenPadding.includes('ALL2'),
      'sm:sui-pl-2': smlScreenPadding.includes('L2'),
      'sm:sui-pr-2': smlScreenPadding.includes('R2'),
      'sm:sui-pt-2': smlScreenPadding.includes('T2'),
      'sm:sui-pb-2': smlScreenPadding.includes('B2'),
      'sui-w-full': fullwidth,
      'sui-block': !display,
    });

  return (
    <div
      className={podSpacerClasses}
      style={style}
      data-testid="pod-spacer"
    >
      {renderChildren(children)}
    </div>
  );
};

PodSpacer.propTypes = {
  /**
   * Padding numbers match spacing scale in stencil design system.
   * Examples: Left -> sui-pl-1, L2 -> sui-pl-2, ALL4 -> sui-p-4
   */
  padding: PropTypes.arrayOf(PropTypes.oneOf([
    'LEFT', 'RIGHT', 'TOP', 'BOTTOM', 'ALL',
    'L2', 'R2', 'T2', 'B2', 'ALL2',
    'L4', 'R4', 'T4', 'B4', 'ALL4'
  ])),
  smlScreenPadding: PropTypes.arrayOf(PropTypes.oneOf([
    'LEFT', 'RIGHT', 'TOP', 'BOTTOM', 'ALL',
    'L2', 'R2', 'T2', 'B2', 'ALL2',
  ])),
  // see Stencil Design System > Foundations > Spacing for correct values
  minHeight: PropTypes.string,
  margin: PropTypes.string
};
PodSpacer.defaultProps = {
  padding: [],
  smlScreenPadding: [],
  minHeight: null,
  margin: null
};
